import React from 'react';
import CopyButton from './CopyButton';

interface PlatformPreviewProps {
  text: string;
  platform?: 'all' | 'instagram' | 'discord' | 'twitter' | 'facebook' | 'imessage' | 'telegram';
}

interface SinglePreviewProps {
  text: string;
}

// iPhone Messages Preview
function IMessagePreview({ text }: SinglePreviewProps): React.ReactElement {
  const displayText = text || 'Your fancy text here';

  return (
    <div className="platform-preview imessage-preview">
      <div className="preview-label">
        <span className="preview-icon">💬</span>
        iMessage
      </div>
      <div className="imessage-container">
        <div className="imessage-header">
          <div className="imessage-status-bar">
            <span className="imessage-time">9:41</span>
            <div className="imessage-notch"></div>
            <div className="imessage-icons">
              <span>📶</span>
              <span>🔋</span>
            </div>
          </div>
          <div className="imessage-nav">
            <span className="imessage-back">‹</span>
            <div className="imessage-contact">
              <div className="imessage-avatar">👤</div>
              <span className="imessage-name">Friend</span>
            </div>
            <span className="imessage-video">📹</span>
          </div>
        </div>
        <div className="imessage-chat">
          <div className="imessage-bubble received">Hey, check this out!</div>
          <div className="imessage-bubble sent">{displayText}</div>
        </div>
      </div>
      <CopyButton text={displayText} small />
    </div>
  );
}

// Instagram Bio Preview
function InstagramPreview({ text }: SinglePreviewProps): React.ReactElement {
  const displayText = text || 'Your fancy text here';

  return (
    <div className="platform-preview instagram-preview">
      <div className="preview-label">
        <span className="preview-icon">📸</span>
        Instagram Bio
      </div>
      <div className="instagram-container">
        <div className="instagram-header">
          <div className="instagram-avatar">
            <span>👤</span>
          </div>
          <div className="instagram-stats">
            <div className="instagram-stat">
              <strong>128</strong>
              <span>posts</span>
            </div>
            <div className="instagram-stat">
              <strong>1.2K</strong>
              <span>followers</span>
            </div>
            <div className="instagram-stat">
              <strong>456</strong>
              <span>following</span>
            </div>
          </div>
        </div>
        <div className="instagram-bio">
          <div className="instagram-username">yourname</div>
          <div className="instagram-bio-text">{displayText}</div>
          <div className="instagram-link">fancymytext.com</div>
        </div>
        <div className="instagram-actions">
          <button className="instagram-follow-btn">Follow</button>
          <button className="instagram-message-btn">Message</button>
        </div>
      </div>
      <CopyButton text={displayText} small />
    </div>
  );
}

// Discord Message Preview
function DiscordPreview({ text }: SinglePreviewProps): React.ReactElement {
  const displayText = text || 'Your fancy text here';

  return (
    <div className="platform-preview discord-preview">
      <div className="preview-label">
        <span className="preview-icon">🎮</span>
        Discord
      </div>
      <div className="discord-container">
        <div className="discord-sidebar">
          <div className="discord-server active">🎯</div>
          <div className="discord-server">🎮</div>
          <div className="discord-server">💬</div>
        </div>
        <div className="discord-main">
          <div className="discord-header">
            <span className="discord-hash">#</span>
            <span className="discord-channel">general</span>
          </div>
          <div className="discord-messages">
            <div className="discord-message">
              <div className="discord-msg-avatar">🤖</div>
              <div className="discord-msg-content">
                <div className="discord-msg-header">
                  <span className="discord-msg-name">FancyBot</span>
                  <span className="discord-msg-time">Today at 4:20 PM</span>
                </div>
                <div className="discord-msg-text">{displayText}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CopyButton text={displayText} small />
    </div>
  );
}

// Twitter/X Post Preview
function TwitterPreview({ text }: SinglePreviewProps): React.ReactElement {
  const displayText = text || 'Your fancy text here';

  return (
    <div className="platform-preview twitter-preview">
      <div className="preview-label">
        <span className="preview-icon">𝕏</span>
        Twitter / X
      </div>
      <div className="twitter-container">
        <div className="twitter-post">
          <div className="twitter-avatar">👤</div>
          <div className="twitter-content">
            <div className="twitter-header">
              <span className="twitter-name">Your Name</span>
              <span className="twitter-handle">@yourhandle</span>
              <span className="twitter-dot">·</span>
              <span className="twitter-time">2h</span>
            </div>
            <div className="twitter-text">{displayText}</div>
            <div className="twitter-actions">
              <span className="twitter-action">💬 12</span>
              <span className="twitter-action">🔁 34</span>
              <span className="twitter-action">❤️ 256</span>
              <span className="twitter-action">📤</span>
            </div>
          </div>
        </div>
      </div>
      <CopyButton text={displayText} small />
    </div>
  );
}

// Facebook Post Preview
function FacebookPreview({ text }: SinglePreviewProps): React.ReactElement {
  const displayText = text || 'Your fancy text here';

  return (
    <div className="platform-preview facebook-preview">
      <div className="preview-label">
        <span className="preview-icon">📘</span>
        Facebook
      </div>
      <div className="facebook-container">
        <div className="facebook-post">
          <div className="facebook-header">
            <div className="facebook-avatar">👤</div>
            <div className="facebook-info">
              <span className="facebook-name">Your Name</span>
              <span className="facebook-time">2 hours ago · 🌐</span>
            </div>
            <span className="facebook-more">•••</span>
          </div>
          <div className="facebook-text">{displayText}</div>
          <div className="facebook-stats">
            <span>👍❤️ 42</span>
            <span>5 comments · 3 shares</span>
          </div>
          <div className="facebook-actions">
            <button className="facebook-action">👍 Like</button>
            <button className="facebook-action">💬 Comment</button>
            <button className="facebook-action">↗️ Share</button>
          </div>
        </div>
      </div>
      <CopyButton text={displayText} small />
    </div>
  );
}

// Telegram Message Preview
function TelegramPreview({ text }: SinglePreviewProps): React.ReactElement {
  const displayText = text || 'Your fancy text here';

  return (
    <div className="platform-preview telegram-preview">
      <div className="preview-label">
        <span className="preview-icon">✈️</span>
        Telegram
      </div>
      <div className="telegram-container">
        <div className="telegram-header">
          <span className="telegram-back">←</span>
          <div className="telegram-contact">
            <div className="telegram-avatar">👤</div>
            <div className="telegram-info">
              <span className="telegram-name">Friend</span>
              <span className="telegram-status">online</span>
            </div>
          </div>
          <div className="telegram-actions">
            <span>📞</span>
            <span>⋮</span>
          </div>
        </div>
        <div className="telegram-chat">
          <div className="telegram-bubble received">
            <span className="telegram-text">What's up?</span>
            <span className="telegram-time">10:30</span>
          </div>
          <div className="telegram-bubble sent">
            <span className="telegram-text">{displayText}</span>
            <span className="telegram-time">10:31 ✓✓</span>
          </div>
        </div>
      </div>
      <CopyButton text={displayText} small />
    </div>
  );
}

// Main component that shows all or specific previews
function PlatformPreview({ text, platform = 'all' }: PlatformPreviewProps): React.ReactElement {
  if (platform !== 'all') {
    switch (platform) {
      case 'instagram':
        return <InstagramPreview text={text} />;
      case 'discord':
        return <DiscordPreview text={text} />;
      case 'twitter':
        return <TwitterPreview text={text} />;
      case 'facebook':
        return <FacebookPreview text={text} />;
      case 'imessage':
        return <IMessagePreview text={text} />;
      case 'telegram':
        return <TelegramPreview text={text} />;
      default:
        return <InstagramPreview text={text} />;
    }
  }

  return (
    <section className="platform-previews-section">
      <h2>See How Your Text Looks</h2>
      <p className="section-subtitle">
        Preview your fancy text on different platforms before you copy
      </p>
      <div className="platform-previews-grid">
        <InstagramPreview text={text} />
        <TwitterPreview text={text} />
        <DiscordPreview text={text} />
        <FacebookPreview text={text} />
        <TelegramPreview text={text} />
        <IMessagePreview text={text} />
      </div>
    </section>
  );
}

export default PlatformPreview;
export { InstagramPreview, TwitterPreview, DiscordPreview, FacebookPreview, IMessagePreview, TelegramPreview };
